/**
 * Client-Side Encryption Utilities
 *
 * Provides AES-GCM encryption for PHI data stored in localStorage/sessionStorage.
 * Uses Web Crypto API for secure encryption.
 */

import logger from './logger';

const ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 256;
const IV_LENGTH = 12;
const KEY_STORAGE_NAME = '_ek';

/**
 * Generate a new encryption key
 */
export async function generateKey() {
  return await window.crypto.subtle.generateKey(
    { name: ALGORITHM, length: KEY_LENGTH },
    true,
    ['encrypt', 'decrypt']
  );
}

/**
 * Export key to storable format
 */
async function exportKey(key) {
  const exported = await window.crypto.subtle.exportKey('raw', key);
  return btoa(String.fromCharCode(...new Uint8Array(exported)));
}

/**
 * Import key from stored format
 */
async function importKey(keyData) {
  const rawKey = Uint8Array.from(atob(keyData), c => c.charCodeAt(0));
  return await window.crypto.subtle.importKey(
    'raw',
    rawKey,
    { name: ALGORITHM, length: KEY_LENGTH },
    true,
    ['encrypt', 'decrypt']
  );
}

/**
 * Get or create encryption key
 */
async function getKey() {
  let keyData = sessionStorage.getItem(KEY_STORAGE_NAME);

  if (!keyData) {
    const key = await generateKey();
    keyData = await exportKey(key);
    sessionStorage.setItem(KEY_STORAGE_NAME, keyData);
    return key;
  }

  return await importKey(keyData);
}

/**
 * Encrypt data
 * @param {any} data - Data to encrypt (will be JSON stringified)
 * @returns {string} - Encrypted data as base64 string
 */
export async function encryptData(data) {
  try {
    const key = await getKey();
    const iv = window.crypto.getRandomValues(new Uint8Array(IV_LENGTH));
    const encoded = new TextEncoder().encode(JSON.stringify(data));

    const encrypted = await window.crypto.subtle.encrypt(
      { name: ALGORITHM, iv },
      key,
      encoded
    );

    // Combine IV and encrypted data
    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encrypted), iv.length);

    return JSON.stringify({
      _encrypted: true,
      _v: 1,
      data: btoa(String.fromCharCode(...combined))
    });
  } catch (error) {
    logger.error('[Crypto] Encryption failed');
    throw error;
  }
}

/**
 * Decrypt data
 * @param {string} encryptedString - Encrypted data string
 * @returns {any} - Decrypted data
 */
export async function decryptData(encryptedString) {
  try {
    const parsed = JSON.parse(encryptedString);

    // Check if data is encrypted
    if (!parsed._encrypted) {
      return parsed;
    }

    const key = await getKey();
    const combined = Uint8Array.from(atob(parsed.data), c => c.charCodeAt(0));

    // Extract IV and encrypted data
    const iv = combined.slice(0, IV_LENGTH);
    const encrypted = combined.slice(IV_LENGTH);

    const decrypted = await window.crypto.subtle.decrypt(
      { name: ALGORITHM, iv },
      key,
      encrypted
    );

    return JSON.parse(new TextDecoder().decode(decrypted));
  } catch (error) {
    logger.error('[Crypto] Decryption failed:', error);
    // Try parsing as plain JSON
    try {
      return JSON.parse(encryptedString);
    } catch {
      return null;
    }
  }
}

/**
 * Secure storage wrapper for sessionStorage
 */
export const secureStorage = {
  async setItem(key, value) {
    const encrypted = await encryptData(value);
    sessionStorage.setItem(key, encrypted);
  },

  async getItem(key) {
    const encrypted = sessionStorage.getItem(key);
    if (!encrypted) return null;
    return await decryptData(encrypted);
  },

  removeItem(key) {
    sessionStorage.removeItem(key);
  },

  clear() {
    const encKey = sessionStorage.getItem(KEY_STORAGE_NAME);
    sessionStorage.clear();
    if (encKey) sessionStorage.setItem(KEY_STORAGE_NAME, encKey);
  }
};

/**
 * Secure storage wrapper for localStorage (use with caution for PHI)
 */
export const secureLocalStorage = {
  async setItem(key, value) {
    const encrypted = await encryptData(value);
    localStorage.setItem(key, encrypted);
  },

  async getItem(key) {
    const encrypted = localStorage.getItem(key);
    if (!encrypted) return null;
    return await decryptData(encrypted);
  },

  removeItem(key) {
    localStorage.removeItem(key);
  }
};

export default { encryptData, decryptData, secureStorage, secureLocalStorage };
